import { create } from 'zustand';
import { ApiError, authApi, hasBearerToken, setBearerToken, setUnauthorizedHandler, type Me, type Role, type Scope } from '../api';
import { queryClient } from '../lib/queryClient';
import { useWorkspace } from './workspace';
import { setPartnerTenant } from '../components/layout/nav';

export type AuthStatus = 'unknown' | 'loading' | 'authenticated' | 'anonymous';
export type LoginStep = 'credentials' | 'totp' | 'organization';

interface AuthState {
  status: AuthStatus;
  me: Me | null;
  step: LoginStep;
  /** id організацій, коли API повертає 409 choose_organization. */
  organizations: string[];
  error: ApiError | null;
  pending: boolean;
  /** Хто був увійшов у цій вкладці до виходу/закінчення сесії: «куди йшли» відновлюємо лише для тієї самої людини. */
  prevUserId: string | null;
  bootstrap: () => Promise<void>;
  login: (email: string, password: string, totp?: string, organizationId?: string) => Promise<boolean>;
  logout: () => Promise<void>;
  resetLogin: () => void;
  expire: () => void;
  setScope: (scope: Scope) => void;
}

export const STAFF_ROLES: Role[] = ['owner', 'manager', 'operator', 'accountant', 'driver', 'partner_admin', 'partner_staff'];

export const useAuth = create<AuthState>((set, get) => ({
  status: 'unknown',
  me: null,
  step: 'credentials',
  organizations: [],
  error: null,
  pending: false,
  prevUserId: null,

  bootstrap: async () => {
    if (get().status === 'loading') return;
    set({ status: 'loading' });
    try {
      let me;
      try {
        me = await authApi.me();
      } catch (e) {
        // збережений токен вкладки прострочений/відкликаний — пробуємо спільну cookie
        if (!(e instanceof ApiError && e.status === 401 && hasBearerToken())) throw e;
        setBearerToken(null);
        me = await authApi.me();
      }
      if (me.role === 'client') {
        set({ status: 'anonymous', me: null });
        return;
      }
      useWorkspace.getState().init(me.workspace);
      set({ status: 'authenticated', me });
    } catch {
      set({ status: 'anonymous', me: null });
    }
  },

  login: async (email, password, totp, organizationId) => {
    set({ pending: true, error: null });
    try {
      const out = await authApi.login({
        email: email.trim(),
        password,
        totp: totp?.trim() || undefined,
        organization_id: organizationId || undefined,
      });
      setBearerToken(out.token);
      const me = await authApi.me();
      // кеш попередньої сесії (інша роль у тому ж браузері) не має потрапити в нову
      queryClient.clear();
      useWorkspace.getState().init(me.workspace);
      set({ status: 'authenticated', me, pending: false, step: 'credentials', organizations: [] });
      return true;
    } catch (e) {
      const err = e instanceof ApiError ? e : new ApiError(0, 'unknown', String(e));
      if (err.code === 'totp_required') {
        set({ step: 'totp', pending: false, error: totp ? err : null });
      } else if (err.code === 'choose_organization') {
        set({
          step: 'organization',
          organizations: (err.detail ?? '').split(',').map((s) => s.trim()).filter(Boolean),
          pending: false,
          error: null,
        });
      } else {
        set({ error: err, pending: false });
      }
      return false;
    }
  },

  logout: async () => {
    try {
      await authApi.logout();
    } catch {
      /* навіть якщо сервер недоступний — локально виходимо */
    }
    setBearerToken(null);
    queryClient.clear();
    useWorkspace.getState().init(null);
    // недописаний звіт «Є проблема?» (components/ReportProblem) не має дістатися наступній людині за цим компʼютером
    try {
      localStorage.removeItem('xq.report.draft');
    } catch {
      /* приватний режим */
    }
    set({ status: 'anonymous', prevUserId: get().me?.user?.id ?? get().prevUserId, me: null, step: 'credentials', error: null });
  },

  resetLogin: () => set({ step: 'credentials', error: null, organizations: [] }),

  setScope: (scope) => {
    const me = get().me;
    if (me) set({ me: { ...me, scope } });
  },

  expire: () => {
    if (get().status !== 'authenticated') return;
    setBearerToken(null);
    queryClient.clear();
    set({ status: 'anonymous', prevUserId: get().me?.user?.id ?? get().prevUserId, me: null, error: new ApiError(401, 'unauthorized') });
  },
}));

// партнерська компанія: меню й Gate ховають продажі/каталог/склад (components/layout/nav.ts)
useAuth.subscribe((s) => setPartnerTenant(!!s.me?.is_partner_company));

setUnauthorizedHandler((path) => {
  if (!path.startsWith('/auth/')) useAuth.getState().expire();
});

export function hasRole(me: Me | null, ...roles: Role[]): boolean {
  return !!me && roles.includes(me.role);
}

/** Окремі права людини (048): 'country_rules' — «редагує правила країн» (owner, менеджери, директор «Справ» — завжди).
 * Сервер перевіряє те саме (403), тут — лише щоб не показувати кнопок, які не спрацюють. */
export function can(me: Me | null, key: 'country_rules'): boolean {
  return !!me?.can?.[key];
}
